<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useLocalizationStore, useSessionStore } from '@/store';
import { handleApiRequest } from '@/api/handleApiRequest';
import { setConfirmedSurgeryDate } from '@/api/endpoints/patientWeb';
import MultiViewLayout from '@/components/layouts/MultiViewLayout.vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import type { ProcedureDate, EpisodeOfCareInfo } from '@/types/webContracts';
import { custom_moment } from '@/plugins/moment-cultured';
import { useDashboardStore } from '@/store';
import { storeToRefs } from 'pinia';
import { loadPatientInfo } from '@/pages/TheDataHydrator';
import Svg from '@/components/Svg.vue';
import { getPatientProcedures } from '@/api/endpoints/userWeb';
import { factorProcedures } from '@/utils/procedureUtils';

const { t } = useI18n();
const $q = useQuasar();
const router = useRouter();
const sessionStore = useSessionStore();
const dashboardStore = useDashboardStore();

const { locale } = storeToRefs(useLocalizationStore());

const props = defineProps({
  selectedEocId: {
    type: Number,
    default: 0,
  },
});

const qDateLocale = computed((): { days: string[]; daysShort: string[]; months: string[]; monthsShort: string[] } => {
  // SA uses a different calendar structure. Use AE calendar for SA.
  const momentLocale = locale.value !== 'ar-SA' ? locale.value : 'ar-AE';
  const ld = custom_moment().locale(momentLocale).localeData();
  return {
    days: ld.weekdays(),
    daysShort: ld.weekdaysShort(),
    months: ld.months(),
    monthsShort: ld.monthsShort(),
  };
});

// Refs
const surgeryDateSelected = ref<string | null>(null);
const selectedDateForDisplay = ref<string>('');
const isDatePickerVisible = ref(false);
const selectedProc = ref<EpisodeOfCareInfo | null>();
const provisionalSurgeryDate = ref<string | null>(null);

const selectedEocId = computed<number>(() => {
  const fromProp = props.selectedEocId ?? 0;
  const fromSession = sessionStore.user?.selectedEocId ?? 0;
  return fromProp > 0 ? Number(fromProp) : Number(fromSession);
});

const globalSelectedProcedure = computed(() => sessionStore.procedures?.find((proc) => proc.eocId === selectedEocId.value));
const pageTitle = computed(() => (globalSelectedProcedure.value?.onWaitlist ? t('add_surgery_date') : t('change_surgery_date')));

const callToActionText = computed(() => {
  return surgeryDateSelected.value ? t('tap_to_edit') : t('tap_to_add');
});

const previousRouteNameText = computed(() => {
  return props.selectedEocId > 0 ? 'Settings' : 'Dashboard.Home';
});

const activeProcedures = computed(() => sessionStore.activeProcedures);

const openDatePicker = () => {
  if (!surgeryDateSelected.value && provisionalSurgeryDate.value) {
    surgeryDateSelected.value = provisionalSurgeryDate.value;
  }
  isDatePickerVisible.value = true;
};

const onSaveClick = async () => {
  const procedureDateInput: ProcedureDate = {
    eocID: Number(selectedProc.value?.eocId ?? 0),
    surgeryDate: custom_moment(surgeryDateSelected.value, 'YYYY/MM/DD').format('YYYY-MM-DD'),
  };
  if (!surgeryDateSelected.value) {
    surgeryDateSelected.value = provisionalSurgeryDate.value;
  }
  $q.loading.show();

  const response = await handleApiRequest(() => setConfirmedSurgeryDate(procedureDateInput), null, $q);

  if (response?.result) {
    const getPatientProceduresRes = await handleApiRequest(() => getPatientProcedures(), null, $q);
    if (getPatientProceduresRes?.result) {
      const episodesOfCare = getPatientProceduresRes.result.episodesOfCare;
      factorProcedures(episodesOfCare, false);
      sessionStore.loadProcedures(episodesOfCare);
    }
    await loadPatientInfo(sessionStore, dashboardStore, $q);
    $q.loading.hide();
    await router.push({ name: previousRouteNameText.value }).catch(() => {});
  }
};

const onDatePicked = (date: string | null) => {
  isDatePickerVisible.value = false;
  if (date) {
    surgeryDateSelected.value = date;
    provisionalSurgeryDate.value = date;
    const parsedDate = custom_moment(date, 'YYYY/MM/DD').toDate();
    if (!isNaN(parsedDate.getTime())) {
      selectedDateForDisplay.value = custom_moment(parsedDate).locale(locale.value).format('MMM D, YYYY');
    }
  } else {
    if (provisionalSurgeryDate.value) {
      surgeryDateSelected.value = provisionalSurgeryDate.value;
      const parsedDate = custom_moment(provisionalSurgeryDate.value, 'YYYY/MM/DD').toDate();
      if (!isNaN(parsedDate.getTime())) {
        selectedDateForDisplay.value = custom_moment(parsedDate).locale(locale.value).format('MMM D, YYYY');
      }
    } else {
      surgeryDateSelected.value = null;
      selectedDateForDisplay.value = '';
    }
  }
};

const isSaveButtonVisible = computed(() => {
  // The save button is visible if a procedure's surgery date is selected and it differs
  // from the currently stored surgery date.
  if (!selectedProc.value?.surgeryDate || !surgeryDateSelected.value) {
    return false;
  }
  const procSurgeryDateFormatted = custom_moment.utc(selectedProc.value.surgeryDate).locale(locale.value).format('YYYY/MM/DD');
  return (
    procSurgeryDateFormatted !== surgeryDateSelected.value ||
    (procSurgeryDateFormatted === surgeryDateSelected.value && selectedProc.value.onWaitlist)
  );
});

const onSelectProcedure = (procedure: EpisodeOfCareInfo) => {
  selectedDateForDisplay.value = '';
  provisionalSurgeryDate.value = null;
  surgeryDateSelected.value = null;
  if (procedure.surgeryDate && procedure?.onWaitlist) return;
  const base = custom_moment.utc(procedure.surgeryDate).locale(locale.value);
  if (base.isValid()) {
    selectedDateForDisplay.value = base.format('MMM D, YYYY');
    provisionalSurgeryDate.value = base.format('YYYY/MM/DD');
    surgeryDateSelected.value = provisionalSurgeryDate.value;
  }
};

function formatDateWithLocale(date: Date | string | null | undefined, locale: string, formate: string = 'MMM D, YYYY'): string {
  if (!date) return '';
  const m = typeof date === 'string' ? custom_moment(date) : custom_moment(date);
  if (!m.isValid()) return '';
  return m.locale(locale).format(formate);
}

const ensureWaitlistAccess = () => {
  if (!dashboardStore.patientConfig?.features?.isWaitlistEnabled || sessionStore.activeProcedures.length === 0) {
    router.push({ name: 'Dashboard.Home' });
  }
};

watch(
  () => sessionStore.procedures,
  () => {
    ensureWaitlistAccess();
    initializeSurgeryDateValues();
  }
);

const initializeSurgeryDateValues = () => {
  selectedProc.value = globalSelectedProcedure.value;
  const procedureSurgeryDate = selectedProc.value?.surgeryDate;
  if (procedureSurgeryDate && !selectedProc.value?.onWaitlist) {
    const base = custom_moment.utc(procedureSurgeryDate).locale(locale.value);

    if (base.isValid()) {
      selectedDateForDisplay.value = base.format('MMM D, YYYY');
      provisionalSurgeryDate.value = base.format('YYYY/MM/DD');
      surgeryDateSelected.value = provisionalSurgeryDate.value;
    }
  }
};

onMounted(() => {
  if (sessionStore.procedures) {
    ensureWaitlistAccess();
    initializeSurgeryDateValues();
  }
});
</script>

<template>
  <q-page padding class="timezone surgerydate-page">
    <MultiViewLayout back-icon-list="fa-solid fa-house-circle-xmark">
      <template #heading>
        <zbm-skeleton-loader height="34px" :loading="dashboardStore.isLoading" width="228px">
          {{ pageTitle }}
        </zbm-skeleton-loader>
      </template>

      <template #default>
        <zbm-skeleton-loader height="500px" :loading="dashboardStore.isLoading" width="100%">
          <div class="column flex-center bedtime-select q-pb-xl">
            <p class="surgery-header text-left">
              {{ t('surgery_date_picker_header') }}
            </p>

            <q-select
              v-if="activeProcedures && activeProcedures.length > 1"
              v-model="selectedProc"
              :options="activeProcedures"
              :display-value="selectedProc?.lateralityBodyPartLocalized"
              class="q-form-el--full align-items-center mb-30 surgery-select-dropdown"
              @update:model-value="onSelectProcedure"
              dropdown-icon="fa-solid fa-bars"
              color="teal"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section avatar>
                    <div class="col-auto">
                      <q-avatar class="no-border" size="50px">
                        <Svg
                          v-if="typeof scope.opt?.bodyPartIcon === 'object'"
                          :view-box="scope.opt?.bodyPartIcon?.viewBox"
                          :vector-paths="scope.opt?.bodyPartIcon?.vectorPaths"
                        />

                        <q-icon
                          v-else-if="typeof scope.opt?.bodyPartIcon === 'string'"
                          :name="scope.opt?.bodyPartIcon"
                          size="24px"
                        />
                      </q-avatar>
                    </div>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ scope.opt.lateralityBodyPartLocalized }}</q-item-label>
                    <q-item-label caption>{{ formatDateWithLocale(scope.opt?.surgeryDate, locale) }}</q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>

            <div class="highlighted-box date-select">
              <div class="col q-pb-md label highlighted-box__label date-select__label" style="text-align: center">
                {{ t('procedure_scheduled_for') }}
              </div>
              <div class="row q-pb-md justify-center" style="display: flex; flex-direction: column; align-items: center">
                <div
                  class="highlighted-box__date-input date-select__input"
                  style="width: 100%; display: flex; justify-content: center"
                  @click="openDatePicker"
                >
                  <q-input
                    v-model="selectedDateForDisplay"
                    style="font-weight: 600; font-size: 40px; min-width: 320px; max-width: 480px; width: 100%"
                    input-class="text-center"
                    readonly
                  >
                  </q-input>
                </div>
                <q-dialog v-model="isDatePickerVisible" @before-hide="isDatePickerVisible = false">
                  <div style="min-width: 320px; max-width: 400px; margin: 0 auto; background: transparent; box-shadow: none">
                    <q-date
                      v-model="surgeryDateSelected"
                      minimal
                      color="primitives-values-100"
                      :locale="qDateLocale"
                      flat
                      @update:model-value="onDatePicked"
                    />
                  </div>
                </q-dialog>
              </div>
              <div class="col call-to-action highlighted-box__call-to-action date-select__cta" style="text-align: center">
                <span
                  @click="openDatePicker"
                  style="
                    cursor: pointer;
                    background: none;
                    border: none;
                    padding: 0;
                    font: inherit;
                    color: inherit;
                    text-decoration: none;
                  "
                  tabindex="0"
                  role="button"
                  @keydown.enter="openDatePicker"
                  @keydown.space="openDatePicker"
                >
                  {{ callToActionText }}
                </span>
              </div>
            </div>
            <div class="surgery-disclaimer" v-html="t('add_surgery_date_disclaimer')"></div>
          </div>
          <div class="col self-end" style="margin-top: 40px">
            <zbm-btn
              v-if="isSaveButtonVisible"
              :color="$zb.enums.ButtonStdColorEnum.accent"
              :label="t('gen_save')"
              :outline="true"
              :width="$zb.enums.ButtonWidthEnum.wFull"
              :height="$zb.enums.ButtonHeightEnum.lg"
              @click="onSaveClick"
            />
          </div>
        </zbm-skeleton-loader>
      </template>
    </MultiViewLayout>
  </q-page>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'sass:math';

.surgerydate-page {
  .surgery-header {
    width: 80%;
  }

  .surgery-disclaimer {
    width: 80%;
    text-align: center;
  }

  .date-select-box {
    .date-input-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      border: 2px solid $wait-list-shadow-box-color;
      border-radius: $generic-border-radius;
      padding: 24px 0 8px 0;
      background: $white;
      margin-bottom: 0;

      .date-input-center {
        width: 100%;
        font-size: 48px;
        text-align: center;

        :deep(.q-field__native) {
          text-align: center;
          font-size: 48px;
          padding: 0;
        }
      }

      .tap-to-edit-text {
        font-size: 20px;
        font-style: italic;
        font-weight: 700;
        color: #888;
        margin-top: 8px;
        text-align: center;
        cursor: pointer;
        user-select: none;
      }
    }
  }

  .highlighted-box {
    background: $white;
    border: 2px solid $chat-message-sent-bg;
    border-radius: 16px;
    padding: 32px 24px 24px 24px;
    margin-bottom: 32px;
    box-shadow:
      0 4px 24px 0 rgba(255, 255, 255, 1),
      0 1.5px 6px 0 rgba(0, 0, 0, 0.04);
    box-sizing: border-box;
    max-width: 100%;
    overflow: hidden;

    &__call-to-action {
      font-style: italic;
      font-weight: map.get($text-weights, bold);
      font-size: map.get($font-sizes, '20');
      line-height: map.get($line-heights, '24');
      color: map.get($co-brand-clrs, content-txt-dta-viz-lbl);
      text-align: center;
      margin-block-end: 40px;
    }

    &__date-input {
      width: 100%;
      font-size: 48px;
      text-align: center;

      :deep(.q-field__native) {
        text-align: center;
        font-size: 48px;
        padding: 0;
      }
    }
  }

  .date-select {
    &__label {
      // reserved for future label-specific styles
    }
    &__input {
      width: 100%;
      :deep(.q-field__native) {
        text-align: center;
        padding: 0;
      }
    }
    &__cta {
      font-style: italic;
      font-weight: map.get($text-weights, bold);
      font-size: map.get($font-sizes, '20');
      line-height: map.get($line-heights, '24');
      color: map.get($co-brand-clrs, content-txt-dta-viz-lbl);
    }
  }

  @media (min-width: 526px) {
    .highlighted-box {
      padding: 40px 32px 32px 32px;
    }
    .date-select__input {
      max-width: 480px;
      margin: 0 auto;
    }
  }

  @media (max-width: 375px) {
    .highlighted-box {
      padding: 24px 16px 16px 16px;
    }
    .date-select__input {
      max-width: 100%;
      margin: 0 auto;

      .q-field {
        min-width: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      :deep(.q-field__native) {
        font-size: 32px;
      }
    }
  }

  &.timezone .bedtime-select :deep(.q-field__native) {
    color: $content-trg-inline-link;
    font-weight: 500;
    padding-bottom: 0;
    line-height: 1;
    border-block-end: 2px solid $cnt-def-page-divider;
  }

  .surgery-select-dropdown {
    width: 80%;
    :deep(.q-field__native)  {
      padding: 0 0 10px 10px;
      border-left: 2px solid $cnt-def-page-divider;
      border-top: 2px solid $cnt-def-page-divider;
      border-top-left-radius: $generic-border-radius;
      border-bottom-left-radius: $generic-border-radius;
      color: var(--content-text-title) !important;
      font-weight: map.get($text-weights, bold);
    }

    :deep(.q-field__append) {
      border-block-end: 2px solid $cnt-def-page-divider;
      padding: 0 10px 0 0;
      border-right: 2px solid $cnt-def-page-divider;
      border-top: 2px solid $cnt-def-page-divider;
      border-top-right-radius: $generic-border-radius;
      border-bottom-right-radius: $generic-border-radius;
    }

    :deep(.q-field__inner) {
      border: 2px solid $cnt-def-page-divider;
      border-bottom: none;
      border: 5px;
    }

    :deep(.q-field__inner){ .ellipsis {
      overflow: unset;
      }
    }
  }
}
</style>
